'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  siteNumber?: string;
  breadcrumbs?: { label: string; href?: string }[];
  actions?: React.ReactNode;
}

export default function AdminLayout({
  children,
  title,
  subtitle,
  siteNumber,
  breadcrumbs = [],
  actions,
}: AdminLayoutProps) {
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !user) router.replace('/login');
  }, [loading, user, router]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fas fa-spinner fa-spin fa-2x text-muted" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="wrapper">
      <Header siteNumber={siteNumber} />
      <Sidebar />

      <div className="content-wrapper">
        {/* Page Header */}
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-sm-6">
                <h1 className="m-0">{title}</h1>
                {subtitle && (
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem', marginTop: 2 }}>
                    {subtitle}
                  </p>
                )}
              </div>
              <div className="col-sm-6">
                <div className="d-flex justify-content-sm-end align-items-center gap-2">
                  {actions && <div className="mr-2">{actions}</div>}
                  <ol className="breadcrumb float-sm-right mb-0">
                    <li className="breadcrumb-item">
                      <a href="/">
                        <i className="fas fa-home" />
                      </a>
                    </li>
                    {breadcrumbs.map((b, i) =>
                      b.href ? (
                        <li key={i} className="breadcrumb-item">
                          <a href={b.href}>{b.label}</a>
                        </li>
                      ) : (
                        <li key={i} className="breadcrumb-item active">
                          {b.label}
                        </li>
                      )
                    )}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="content">
          <div className="container-fluid">{children}</div>
        </div>
      </div>

      <Footer />

      {/* Control Sidebar */}
      <aside className="control-sidebar control-sidebar-dark" />
    </div>
  );
}
